// Sentry MUST be initialised before any other import so the global
// JS error handlers + React error boundaries wrap every render that
// follows. No-ops cleanly when REACT_APP_SENTRY_DSN is missing.
import { installSentry, SentryErrorBoundary, sentryReactErrorHandler } from "@/lib/sentry";
installSentry();

import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";

function RootErrorFallback() {
  // Minimal, brand-styled fallback — never leaks the underlying error
  // text to users. The actual error is already reported to Sentry.
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#1D78AB",
        color: "white",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        fontFamily: "system-ui, sans-serif",
      }}
      data-testid="root-error-fallback"
    >
      <div style={{ maxWidth: 360, textAlign: "center" }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          Bidorah ran into a hiccup
        </h1>
        <p style={{ opacity: 0.9, fontSize: 15, lineHeight: 1.5 }}>
          We&apos;ve been notified and are looking into it. Tap below to reload.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 20,
            padding: "12px 28px",
            border: "none",
            borderRadius: 999,
            background: "white",
            color: "#1D78AB",
            fontWeight: 800,
            fontSize: 15,
            cursor: "pointer",
          }}
          data-testid="root-error-reload-btn"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"), {
  // React 18+ error hooks → Sentry. Captures uncaught render errors,
  // errors caught by boundaries, and recoverable errors with their
  // React component stacks intact.
  onUncaughtError: sentryReactErrorHandler(),
  onCaughtError: sentryReactErrorHandler(),
  onRecoverableError: sentryReactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<RootErrorFallback />} showDialog={false}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for PWA install + offline shell (production only)
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Aggressively check for SW updates every time the app comes back
        // to the foreground. Default browser behavior is to check once
        // every 24h, which is far too slow for an actively-deployed app
        // — this guarantees a fresh SW is installed within seconds of a
        // deploy the next time the user backgrounds + reopens the app.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            registration.update().catch(() => { /* ignore */ });
          }
        });
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}
